import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PROJECTS } from "@/data/projects";

const ALL = "All";

// Rotating aspect ratios for visual rhythm regardless of filter length
const ASPECTS = [
  "col-span-12 md:col-span-7 aspect-[16/10]",
  "col-span-12 md:col-span-5 aspect-[4/5]",
  "col-span-12 md:col-span-4 aspect-[4/5]",
  "col-span-12 md:col-span-8 aspect-[16/10]",
  "col-span-12 md:col-span-6 aspect-[4/3]",
  "col-span-12 md:col-span-6 aspect-[4/3]",
  "col-span-12 md:col-span-4 aspect-[4/5]",
  "col-span-12 md:col-span-4 aspect-[4/5]",
  "col-span-12 md:col-span-4 aspect-[4/5]",
];

export default function SelectedWork() {
  const categories = useMemo(() => {
    const counts = new Map();
    PROJECTS.forEach((p) => counts.set(p.category, (counts.get(p.category) || 0) + 1));
    const list = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
    return [{ name: ALL, count: PROJECTS.length }, ...list];
  }, []);

  const [active, setActive] = useState(ALL);

  const filtered = useMemo(
    () => (active === ALL ? PROJECTS : PROJECTS.filter((p) => p.category === active)),
    [active]
  );

  return (
    <section id="work" data-testid="selected-work-section" className="px-4 md:px-8 lg:px-12 py-20 md:py-28">
      <div className="mb-10 md:mb-14 max-w-6xl">
        <div className="section-label mb-3 text-[#4A4A4A]">§ 05 — Selected Work</div>
        <h2 className="font-anton uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
          Selected
          <span className="font-cormorant italic text-[#D83127] font-semibold"> case </span>
          studies.
        </h2>
        <p className="font-work text-lg mt-5 max-w-xl">
          Fourteen campaigns, rebrands, and activations from the last few years. Click any tile
          for the full case study — the complete archive is available on request.
        </p>
      </div>

      {/* Filter chips */}
      <div
        className="mb-8 md:mb-12 flex flex-wrap items-center gap-2 md:gap-3"
        role="tablist"
        aria-label="Filter selected work by category"
        data-testid="work-filter-bar"
      >
        <span className="font-mono-s text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#4A4A4A] mr-1">
          Filter /
        </span>
        {categories.map((c) => {
          const selected = active === c.name;
          return (
            <button
              key={c.name}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(c.name)}
              data-testid={`work-filter-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`group relative font-mono-s text-[10px] md:text-xs uppercase tracking-[0.2em] px-3 py-2 border transition-all duration-200 ${
                selected
                  ? "bg-[#111111] text-[#F2EFE9] border-[#111111]"
                  : "bg-transparent text-[#111111] border-[#111111] hover:bg-[#E8E4DB]"
              }`}
              style={
                selected ? { boxShadow: "3px 3px 0 #D83127" } : undefined
              }
            >
              {c.name}
              <span
                className={`ml-2 font-anton text-[11px] md:text-xs ${
                  selected ? "text-[#D83127]" : "text-[#4A4A4A]"
                }`}
              >
                {String(c.count).padStart(2, "0")}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        <div
          className="font-mono-s text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#4A4A4A]"
          data-testid="work-filter-count"
          aria-live="polite"
        >
          Showing {String(filtered.length).padStart(2, "0")} of{" "}
          {String(PROJECTS.length).padStart(2, "0")}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6 min-h-[40vh]">
        <AnimatePresence mode="popLayout">
          {filtered.map((p, i) => (
            <motion.div
              key={p.slug}
              layout
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.05, layout: { duration: 0.4 } }}
              className={ASPECTS[i % ASPECTS.length]}
              data-testid={`work-tile-wrapper-${p.slug}`}
            >
              <Link
                to={`/project/${p.slug}`}
                className="work-tile block w-full h-full"
                data-testid={`work-tile-${i}`}
                aria-label={`${p.title} — ${p.category}`}
              >
                <img src={p.hero} alt={p.title} loading="lazy" />
                <div className="tile-overlay">
                  <div className="flex items-center gap-3 font-mono-s text-[10px] md:text-xs tracking-[0.25em] uppercase mb-2">
                    <span>{p.category}</span>
                    <span>/</span>
                    <span>{p.year}</span>
                  </div>
                  <div className="font-anton uppercase text-3xl md:text-4xl lg:text-5xl leading-[0.95]">
                    {p.title}
                  </div>
                  <div className="font-cormorant italic text-base md:text-lg mt-2 max-w-md">
                    {p.summary}
                  </div>
                  <div className="mt-3 font-mono-s text-xs uppercase tracking-[0.3em]">Read Case →</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <div
          className="hard-border p-10 mt-4 text-center"
          data-testid="work-empty-state"
        >
          <div className="font-anton uppercase text-3xl md:text-4xl">Nothing here yet.</div>
          <p className="font-cormorant italic text-lg mt-2">
            Try a different filter — or just email JP.
          </p>
        </div>
      )}
    </section>
  );
}
